import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonatingName, setImpersonatingName] = useState(() => localStorage.getItem("ofertas_impersonating"));

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("ofertas_token");
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem("ofertas_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (whatsapp, password) => {
    const { data } = await api.post("/auth/login-business", { whatsapp, password });
    localStorage.setItem("ofertas_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const loginAdmin = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ofertas_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const loginCustomer = useCallback(async (whatsapp, password) => {
    const { data } = await api.post("/auth/login-customer", { whatsapp, password });
    localStorage.setItem("ofertas_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const loginPromoter = useCallback(async (phone, password) => {
    const { data } = await api.post("/auth/login-promoter", { phone, password });
    localStorage.setItem("ofertas_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const registerPromoter = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register-promoter", payload);
    localStorage.setItem("ofertas_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("ofertas_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const registerCustomer = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register-customer", payload);
    localStorage.setItem("ofertas_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("ofertas_token");
    localStorage.removeItem("ofertas_admin_backup");
    localStorage.removeItem("ofertas_impersonating");
    setImpersonatingName(null);
    setUser(null);
  }, []);

  // Admin: enter another account's backoffice. Full reload resets all state cleanly.
  const impersonate = useCallback(async (targetType, targetId) => {
    const { data } = await api.post(`/admin/impersonate/${targetType}/${targetId}`);
    const current = localStorage.getItem("ofertas_token");
    if (current) localStorage.setItem("ofertas_admin_backup", current);
    localStorage.setItem("ofertas_token", data.access_token);
    localStorage.setItem("ofertas_impersonating", data.name || "cuenta");
    const dest = targetType === "business" ? "/dashboard" : targetType === "promoter" ? "/promotor" : "/mi-cuenta";
    window.location.href = dest;
  }, []);

  const stopImpersonating = useCallback(() => {
    const backup = localStorage.getItem("ofertas_admin_backup");
    localStorage.removeItem("ofertas_admin_backup");
    localStorage.removeItem("ofertas_impersonating");
    if (backup) {
      localStorage.setItem("ofertas_token", backup);
      window.location.href = "/admin";
    } else {
      localStorage.removeItem("ofertas_token");
      window.location.href = "/login";
    }
  }, []);

  const value = useMemo(() => ({
    user, loading, login, loginAdmin, loginCustomer, loginPromoter,
    register, registerCustomer, registerPromoter, logout, refresh,
    impersonatingName, impersonate, stopImpersonating,
  }), [user, loading, login, loginAdmin, loginCustomer, loginPromoter, register, registerCustomer, registerPromoter, logout, refresh, impersonatingName, impersonate, stopImpersonating]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
